import React, { Fragment } from 'react';
import { Shield, Target, Briefcase, Users, ChevronRight, Calendar, ArrowRight, AlertTriangle, Lock } from 'lucide-react';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { SectionHeader } from '../components/ui/SectionHeader';
const MISSION_IMAGE = 'https://drwzpk38qkpfb.cloudfront.net/core/getthumb?file=uploaded%2Fimages%2FRedesigne+2024%2FStrategic-Planning-for-Crisis-Situations.jpeg700x700.jpeg&h=446&opt=5&site=1856&user=1516&w=700';
const microOfferings = ['Policy Review', 'Gap Analysis', 'Policy Development', 'Operational Plans', 'Risk Management'];
const quickTracks = [{
  label: 'Law Enforcement',
  href: '/law-enforcement-capabilities'
}, {
  label: 'Corporate',
  href: '/corporate-critical-incidents-planning'
}, {
  label: 'Civilian',
  href: '/civilian-protection'
}];
function CaptchaContainer() {
  return <div className="flex items-center gap-3 p-4 border border-brand-gray-700 bg-brand-gray-900 rounded-sm" data-id="element-111">
      <div className="w-6 h-6 border-2 border-brand-gray-600 rounded-sm flex-shrink-0" data-id="element-112" />
      <span className="text-sm text-brand-gray-400 flex-grow" data-id="element-113">
        I'm not a robot
      </span>
      <div className="flex flex-col items-center gap-0.5" data-id="element-114">
        <Lock className="w-6 h-6 text-brand-gray-600" data-id="element-115" />
        <span className="text-[9px] text-brand-gray-600 leading-none" data-id="element-116">
          reCAPTCHA
        </span>
        <span className="text-[8px] text-brand-gray-700 leading-none" data-id="element-117">
          Privacy · Terms
        </span>
      </div>
    </div>;
}
export function HomePage() {
  return <div className="min-h-screen flex flex-col" data-id="element-118">
      <Navbar data-id="element-119" />

      <main className="flex-grow pt-[7.25rem] md:pt-[7.25rem]" data-id="element-120">
        {/* ── HERO ── */}
        <section className="relative min-h-[90vh] flex items-center tactical-grid border-b border-brand-gray-800" data-id="element-121">
          <div className="absolute inset-0 bg-gradient-to-b from-brand-black/40 via-brand-black/75 to-brand-black z-0" data-id="element-122" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-20" data-id="element-123">
            <div className="max-w-3xl" data-id="element-124">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1 border border-brand-teal/30 bg-brand-teal/10 text-brand-teal text-xs font-bold uppercase tracking-widest mb-8 clip-edges" data-id="element-125">
                <span className="w-2 h-2 bg-brand-teal animate-pulse" data-id="element-126" />
                Be The Standard
              </div>

              {/* H1 */}
              <h1 className="text-5xl md:text-7xl font-extrabold text-brand-white tracking-tight leading-tight mb-4 uppercase" data-id="element-127">
                Strategic{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-teal to-brand-teal-light" data-id="element-128">
                  Preparedness
                </span>
                <br data-id="element-129" />
                for Critical Incidents
              </h1>

              {/* Micro-offerings strip */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-8 pl-1" data-id="element-130">
                {microOfferings.map((item, i) => <Fragment key={item} data-id="element-131">
                    <span className="text-xs font-semibold text-brand-gray-300 uppercase tracking-widest" data-id="element-132">
                      {item}
                    </span>
                    {i < microOfferings.length - 1 && <span className="w-1 h-1 bg-brand-teal rounded-full flex-shrink-0" data-id="element-133" />}
                  </Fragment>)}
              </div>

              {/* Subheadline */}
              <p className="text-xl text-brand-gray-300 mb-6 max-w-2xl font-light border-l-2 border-brand-teal pl-6 leading-relaxed" data-id="element-134">
                Preparation and strategic planning for crisis situations —
                evaluating your existing policies, identifying potential gaps,
                and implementing strategic enhancements with a{' '}
                <strong className="text-brand-white font-semibold" data-id="element-135">
                  proactive approach to risk management
                </strong>{' '}
                tailored to your specific needs.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-10" data-id="element-136">
                <Button href="/crisis-management-planning" variant="primary" size="lg" className="group" data-id="element-137">
                  Crisis Planning
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-138" />
                </Button>
                <Button href="/training" variant="outline" size="lg" data-id="element-139">
                  <Calendar className="w-5 h-5" data-id="element-140" />
                  View Calendar
                </Button>
              </div>

              {/* Tertiary link */}
              <a href="/Contact-Medina Tactical" className="text-sm text-brand-gray-500 hover:text-brand-teal transition-colors uppercase tracking-wider underline underline-offset-4 mb-10 inline-block" data-id="element-141">
                Contact Us
              </a>

              {/* Quick track links */}
              <div className="mt-4 pt-8 border-t border-brand-gray-800" data-id="element-142">
                <p className="text-xs font-bold text-brand-gray-500 uppercase tracking-widest mb-4" data-id="element-143">
                  Choose Your Track
                </p>
                <div className="flex flex-wrap gap-3" data-id="element-144">
                  {quickTracks.map(track => <a key={track.label} href={track.href} className="inline-flex items-center gap-1.5 px-4 py-2 border border-brand-gray-700 text-xs font-semibold text-brand-gray-300 hover:border-brand-teal hover:text-brand-teal uppercase tracking-wider transition-colors clip-edges" data-id="element-145">
                      {track.label}
                      <ChevronRight className="w-3 h-3" data-id="element-146" />
                    </a>)}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── CRISIS PLANNING BRIDGE ── */}
        <section className="py-16 md:py-24 bg-brand-gray-900 border-b border-brand-gray-800 relative" data-id="element-147">
          <div className="absolute inset-0 tactical-grid opacity-40" data-id="element-148" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-149">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mb-12" data-id="element-150">
              {/* Left column */}
              <div className="border border-brand-gray-800 bg-brand-black/50 p-8" data-id="element-151">
                <div className="w-8 h-0.5 bg-brand-teal mb-6" data-id="element-152" />
                <h3 className="text-xl font-bold text-brand-white uppercase tracking-wide mb-6" data-id="element-153">
                  Strategic Preparedness for Critical Incidents
                </h3>
                <ul className="space-y-4" data-id="element-154">
                  {['Evaluating your existing policies to identify strengths and vulnerabilities', 'Identifying potential gaps before a critical incident occurs', 'Implementing strategic enhancements through comprehensive policy development and continuous updates', 'Advanced operational plans tailored to your specific needs'].map(point => <li key={point} className="flex items-start gap-3 text-sm text-brand-gray-400 leading-relaxed" data-id="element-155">
                      <div className="w-1.5 h-1.5 bg-brand-teal flex-shrink-0 mt-1.5" data-id="element-156" />
                      {point}
                    </li>)}
                </ul>
              </div>

              {/* Right column */}
              <div className="border border-brand-gray-800 bg-brand-black/50 p-8" data-id="element-157">
                <div className="w-8 h-0.5 bg-brand-teal mb-6" data-id="element-158" />
                <h3 className="text-xl font-bold text-brand-white uppercase tracking-wide mb-6" data-id="element-159">
                  Proactive Strategy Development for Operational Readiness
                </h3>
                <ul className="space-y-4" data-id="element-160">
                  {['Designing, refining, and implementing forward-thinking strategies for positive outcomes when critical moments arise', 'Tailored operational plans that keep staff well-informed, fully equipped, and ready to respond effectively', 'A proactive approach to risk management that minimizes risks and maximizes efficiency', 'Applying the latest methodologies and cutting-edge technologies to your preparedness planning'].map(point => <li key={point} className="flex items-start gap-3 text-sm text-brand-gray-400 leading-relaxed" data-id="element-161">
                      <div className="w-1.5 h-1.5 bg-brand-teal flex-shrink-0 mt-1.5" data-id="element-162" />
                      {point}
                    </li>)}
                </ul>
              </div>
            </div>

            {/* CTA row */}
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-163">
              <Button href="/crisis-management-planning" variant="primary" size="md" className="group" data-id="element-164">
                Crisis Planning
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-165" />
              </Button>
              <Button href="/Contact-Medina Tactical" variant="outline" size="md" data-id="element-166">
                Contact Us
              </Button>
            </div>
          </div>
        </section>

        {/* ── CORE CAPABILITIES (4-card) ── */}
        <section className="py-16 md:py-24 lg:py-32 relative" data-id="element-167">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-168">
            <SectionHeader title="Core Capabilities" data-id="element-169" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-170">
              {/* Crisis Planning — featured */}
              <Card hoverable className="flex flex-col h-full border-brand-teal/40 bg-brand-teal/5 sm:col-span-2 lg:col-span-1" data-id="element-171">
                <AlertTriangle className="w-12 h-12 text-brand-teal mb-6" data-id="element-172" />
                <div className="text-xs font-bold text-brand-teal uppercase tracking-widest mb-2" data-id="element-173">
                  Featured
                </div>
                <h3 className="text-xl font-bold text-brand-white mb-4 uppercase tracking-wide" data-id="element-174">
                  Crisis Planning
                </h3>
                <p className="text-base text-brand-gray-400 mb-8 flex-grow" data-id="element-175">
                  Preparation and strategic planning for crisis situations —
                  designing, refining, and implementing forward-thinking
                  strategies that minimize risks and maximize efficiency.
                </p>
                <a href="/crisis-management-planning" className="inline-flex items-center text-brand-teal font-bold uppercase tracking-wider text-sm hover:text-brand-teal-light transition-colors group" data-id="element-176">
                  Learn More{' '}
                  <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-177" />
                </a>
              </Card>

              <Card hoverable className="flex flex-col h-full" data-id="element-178">
                <Shield className="w-12 h-12 text-brand-teal mb-6" data-id="element-179" />
                <h3 className="text-xl font-bold text-brand-white mb-4 uppercase tracking-wide" data-id="element-180">
                  Law Enforcement
                </h3>
                <p className="text-base text-brand-gray-400 mb-8 flex-grow" data-id="element-181">
                  Specialized training and consulting for law enforcement
                  professionals.
                </p>
                <a href="/law-enforcement-capabilities" className="inline-flex items-center text-brand-teal font-bold uppercase tracking-wider text-sm hover:text-brand-teal-light transition-colors group" data-id="element-182">
                  Learn More{' '}
                  <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-183" />
                </a>
              </Card>

              <Card hoverable className="flex flex-col h-full" data-id="element-184">
                <Briefcase className="w-12 h-12 text-brand-teal mb-6" data-id="element-185" />
                <h3 className="text-xl font-bold text-brand-white mb-4 uppercase tracking-wide" data-id="element-186">
                  Corporate
                </h3>
                <p className="text-base text-brand-gray-400 mb-8 flex-grow" data-id="element-187">
                  Corporate planning and strategies for critical incidents.
                </p>
                <a href="/corporate-critical-incidents-planning" className="inline-flex items-center text-brand-teal font-bold uppercase tracking-wider text-sm hover:text-brand-teal-light transition-colors group" data-id="element-188">
                  Learn More{' '}
                  <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-189" />
                </a>
              </Card>

              <Card hoverable className="flex flex-col h-full" data-id="element-190">
                <Users className="w-12 h-12 text-brand-teal mb-6" data-id="element-191" />
                <h3 className="text-xl font-bold text-brand-white mb-4 uppercase tracking-wide" data-id="element-192">
                  Civilian
                </h3>
                <p className="text-base text-brand-gray-400 mb-8 flex-grow" data-id="element-193">
                  Consulting information for civilian entities.
                </p>
                <a href="/civilian-protection" className="inline-flex items-center text-brand-teal font-bold uppercase tracking-wider text-sm hover:text-brand-teal-light transition-colors group" data-id="element-194">
                  Learn More{' '}
                  <ChevronRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-195" />
                </a>
              </Card>
            </div>
          </div>
        </section>

        {/* ── MISSION ── */}
        <section id="Mission" className="py-16 md:py-24 lg:py-32 bg-brand-gray-900 border-y border-brand-gray-800" data-id="element-196">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-197">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-198">
              <div data-id="element-199">
                <SectionHeader title="Our Mission" data-id="element-200" />

                <p className="text-xl text-brand-white font-medium mb-6 leading-relaxed" data-id="element-201">
                  Medina Tactical Dynamics is committed to providing our clients
                  with highest quality of service combined with a strong sense
                  of 360° situational awareness.
                </p>

                <div className="space-y-4 text-brand-gray-400 mb-8" data-id="element-202">
                  <p className="text-base" data-id="element-203">
                    Providing quality services to our clients is our top
                    priority. We are available to assist you with your training
                    and consulting needs, delivering current and long-term
                    solutions through innovative ideas, strategies, and
                    preparedness.
                  </p>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6" data-id="element-204">
                    {['Educational Programs', 'Course Development', 'Use of Force Litigation', 'Law Enforcement Consulting', 'Corporate & Civilian Entities', 'Private Investigation'].map(item => <li key={item} className="flex items-center gap-2 text-sm" data-id="element-205">
                        <div className="w-1.5 h-1.5 bg-brand-teal flex-shrink-0" data-id="element-206" />
                        {item}
                      </li>)}
                  </ul>
                </div>

                <div className="p-6 border border-brand-gray-700 bg-brand-black clip-edges" data-id="element-207">
                  <h3 className="text-lg font-semibold text-brand-white uppercase mb-2" data-id="element-208">
                    The Medina Standard
                  </h3>
                  <p className="text-sm text-brand-gray-400 mb-6" data-id="element-209">
                    We operate with a 24/7 mentality in customer support,
                    ensuring you are always prepared for the unexpected.
                  </p>
                  <Button href="/Contact-Medina Tactical" variant="outline" size="sm" data-id="element-210">
                    Contact Us
                  </Button>
                </div>
              </div>

              {/* Real image */}
              <div className="relative h-full min-h-[400px] border border-brand-gray-700 bg-brand-black p-2" data-id="element-211">
                <img src={MISSION_IMAGE} alt="Strategic Planning for Crisis Situations" className="absolute inset-2 w-[calc(100%-1rem)] h-[calc(100%-1rem)] object-cover" data-id="element-212" />
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-brand-teal z-10" data-id="element-213" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-brand-teal z-10" data-id="element-214" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-brand-teal z-10" data-id="element-215" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-brand-teal z-10" data-id="element-216" />
              </div>
            </div>
          </div>
        </section>

        {/* ── UPCOMING EVENTS ── */}
        <section className="py-16 md:py-24 lg:py-32 relative tactical-grid" data-id="element-217">
          <div className="absolute inset-0 bg-brand-black/90 z-0" data-id="element-218" />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-219">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12 md:mb-16 gap-6" data-id="element-220">
              <SectionHeader title="Upcoming Training Events" className="mb-0" data-id="element-221" />
              <Button href="/training" variant="primary" data-id="element-222">
                <Calendar className="w-4 h-4" data-id="element-223" />
                View Calendar
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-224">
              {[{
              title: 'Front Line Patrol Leadership and Training',
              link: '/law-enforcement-capabilities'
            }, {
              title: 'Instructor Development and Education',
              link: '/training'
            }, {
              title: 'Advanced Tactical Training',
              link: '/about-apc'
            }].map((event, i) => <div key={i} className="group border border-brand-gray-800 bg-brand-black p-6 hover:border-brand-teal transition-colors flex flex-col" data-id="element-225">
                  <div className="text-xs font-bold text-brand-teal uppercase tracking-widest mb-4 flex items-center gap-2" data-id="element-226">
                    <span className="w-2 h-2 bg-brand-teal rounded-sm" data-id="element-227" />
                    Featured Course
                  </div>
                  <h3 className="text-xl font-bold text-brand-white mb-6 uppercase leading-snug flex-grow" data-id="element-228">
                    {event.title}
                  </h3>
                  <a href={event.link} className="inline-flex items-center text-brand-gray-400 font-bold uppercase tracking-wider text-sm group-hover:text-brand-teal transition-colors" data-id="element-229">
                    Learn More{' '}
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-230" />
                  </a>
                </div>)}
            </div>
          </div>
        </section>

        {/* ── NEWSLETTER + CONTACT SPLIT ── */}
        <section className="border-t border-brand-gray-800" data-id="element-231">
          <div className="grid grid-cols-1 lg:grid-cols-2" data-id="element-232">
            {/* Newsletter */}
            <div className="bg-brand-gray-900 p-12 lg:p-24 flex flex-col justify-center border-b lg:border-b-0 lg:border-r border-brand-gray-800" data-id="element-233">
              <div className="max-w-md mx-auto w-full" data-id="element-234">
                <h2 className="text-3xl font-bold uppercase tracking-wider mb-4 text-brand-white" data-id="element-235">
                  Stay Prepared
                </h2>
                <p className="text-base text-brand-gray-300 mb-8" data-id="element-236">
                  Subscribe to stay up to date on the latest tactical course and
                  education offerings.
                </p>
                <form className="space-y-4" onSubmit={e => e.preventDefault()} data-id="element-237">
                  <Input label="Email Address" type="email" placeholder="Enter your email" required data-id="element-238" />
                  <CaptchaContainer data-id="element-239" />
                  <Button type="submit" variant="primary" className="w-full" data-id="element-240">
                    Subscribe
                  </Button>
                </form>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-brand-gray-900 p-12 lg:p-24 flex flex-col justify-center" data-id="element-241">
              <div className="max-w-md mx-auto w-full" data-id="element-242">
                <h2 className="text-3xl font-bold uppercase tracking-wider mb-4 text-brand-white" data-id="element-243">
                  Have Questions?
                </h2>
                <p className="text-base text-brand-gray-400 mb-8" data-id="element-244">
                  Reach out to our team for consulting or training inquiries.
                </p>
                <form className="space-y-4" onSubmit={e => e.preventDefault()} data-id="element-245">
                  <Input label="Your Name" type="text" placeholder="John Doe" required data-id="element-246" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-247">
                    <Input label="Email" type="email" placeholder="john@example.com" required data-id="element-248" />
                    <Input label="Phone" type="tel" placeholder="(555) 123-4567" data-id="element-249" />
                  </div>
                  <Input label="What are your needs?" multiline placeholder="Describe your training or consulting needs..." required data-id="element-250" />
                  <CaptchaContainer data-id="element-251" />
                  <Button type="submit" variant="outline" className="w-full text-brand-white border-brand-gray-600 hover:border-brand-teal" data-id="element-252">
                    Contact Us
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-253" />
    </div>;
}